import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOrgSubscription } from '../hooks/useOrgSubscription';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { MessagesSquare, Plus, Code2, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, BACKEND_URL } from '../config';

const FaqsPage = () => {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const { subscription, loading: subLoading } = useOrgSubscription(currentOrg?.id);
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedRow, setEmbedRow] = useState(null);

  const addon = subscription?.faqs_addon;
  const purchasedWidgets = Number(addon?.purchased_widgets) || 0;
  const addonOn = purchasedWidgets > 0 || !!addon?.enabled;
  const maxFaqSets =
    typeof addon?.max_faq_sets === 'number' && !Number.isNaN(addon.max_faq_sets) ? addon.max_faq_sets : 1;
  const usedSets = sets.length;
  const remainingSets = Math.max(0, maxFaqSets - usedSets);
  const canAddMore = usedSets < maxFaqSets;

  const fetchSets = useCallback(async () => {
    if (!currentOrg?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/organizations/${currentOrg.id}/faq-sets`);
      setSets(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load FAQ sets');
      setSets([]);
    } finally {
      setLoading(false);
    }
  }, [currentOrg?.id]);

  useEffect(() => {
    fetchSets();
  }, [fetchSets]);

  const openEmbed = (row) => {
    setEmbedRow(row);
    setEmbedOpen(true);
  };

  const iframeSrc = embedRow
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/embed/faq/${embedRow.public_embed_id}`
    : '';
  const scriptSnippet = embedRow
    ? `<script src="${BACKEND_URL}/api/public/faq-widget.js" data-public-id="${embedRow.public_embed_id}" data-min-height="520" async><\/script>`
    : '';

  const canManage =
    currentOrg?.user_role && ['company', 'member'].includes(currentOrg.user_role);

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] text-slate-900 dark:text-white flex items-center gap-2">
            <MessagesSquare className="w-7 h-7 text-emerald-600" />
            FAQs
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Create FAQ sets and embed them on any site with iframe or script snippets.
          </p>
          {!subLoading ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="font-normal text-sm">
                {usedSets} / {maxFaqSets} sets created
              </Badge>
              <Badge variant={remainingSets > 0 ? 'outline' : 'destructive'} className="font-normal text-sm">
                {remainingSets} more {remainingSets === 1 ? 'set' : 'sets'} available
              </Badge>
            </div>
          ) : null}
        </div>
        {canManage ? (
          canAddMore ? (
            <Button onClick={() => navigate('/dashboard/faqs/new')} className="shrink-0">
              <Plus className="w-4 h-4 mr-2" />
              New FAQ set
            </Button>
          ) : (
            <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0 max-w-sm">
              <Button disabled className="cursor-not-allowed opacity-80">
                <Plus className="w-4 h-4 mr-2" />
                Limit reached
              </Button>
              <p className="text-xs text-slate-500 text-right">
                {addonOn
                  ? `You have ${purchasedWidgets} FAQ widget${purchasedWidgets !== 1 ? 's' : ''} — all in use.`
                  : 'Your free draft set is in use.'}
              </p>
              <Button variant="outline" size="sm" asChild>
                <Link to="/pricing">
                  {addonOn ? 'Buy more FAQ widgets' : 'Get FAQ widgets on Pricing'}
                </Link>
              </Button>
            </div>
          )
        ) : null}
      </div>

      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="font-[Manrope]">Your FAQ sets</CardTitle>
          <CardDescription>
            Each set has its own embed codes. Paste the iframe or script snippet into any HTML page or
            WordPress “Custom HTML” block.
            {!subLoading ? (
              <span className="block mt-2 text-slate-700 dark:text-slate-300">
                You can create <strong>{remainingSets}</strong> more {remainingSets === 1 ? 'set' : 'sets'} (
                <strong>{maxFaqSets}</strong> total allowed for this workspace).
              </span>
            ) : null}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 py-10">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading…
            </div>
          ) : sets.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">
              No FAQ sets yet. Create one — your first set is a free draft. Purchase FAQ Widgets on{' '}
              <Link to="/pricing" className="text-blue-600 hover:underline">
                Pricing
              </Link>{' '}
              to embed FAQs on external sites.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {sets.map((s) => (
                <li
                  key={s.id}
                  className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                >
                  <div>
                    <div className="font-medium text-slate-900 dark:text-white">{s.title || 'Untitled'}</div>
                    {s.subtitle ? (
                      <div className="text-sm text-slate-500 mt-0.5">{s.subtitle}</div>
                    ) : null}
                    <div className="text-xs text-slate-400 font-mono mt-1">ID {s.public_embed_id}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">{s.items?.length || 0} questions</Badge>
                    <Button variant="outline" size="sm" onClick={() => openEmbed(s)}>
                      <Code2 className="w-4 h-4 mr-1" />
                      Embed
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href={`/embed/faq/${s.public_embed_id}`} target="_blank" rel="noreferrer">
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Preview
                      </a>
                    </Button>
                    {canManage ? (
                      <Button size="sm" onClick={() => navigate(`/dashboard/faqs/${s.id}`)}>
                        Edit
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Embed “{embedRow?.title}”</DialogTitle>
            <DialogDescription>
              Use either option. Replace the origin in the iframe URL if your DocTree app is on a
              different host than this dashboard.
            </DialogDescription>
            {!addonOn ? (
              <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md p-3">
                No paid FAQ widgets — public embeds stay unavailable until you purchase FAQ Widgets on Pricing.
              </p>
            ) : null}
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <div className="font-medium text-slate-900 dark:text-white mb-1">Iframe (simplest)</div>
              <pre className="text-xs bg-slate-950 text-slate-100 p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all">
                {`<iframe src="${iframeSrc}" title="FAQ" width="100%" style="border:0;min-height:520px" loading="lazy"></iframe>`}
              </pre>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `<iframe src="${iframeSrc}" title="FAQ" width="100%" style="border:0;min-height:520px" loading="lazy"></iframe>`
                  );
                  toast.success('Iframe code copied');
                }}
              >
                Copy iframe code
              </Button>
            </div>
            <div>
              <div className="font-medium text-slate-900 dark:text-white mb-1">Script (loads iframe automatically)</div>
              <p className="text-xs text-slate-500 mb-1">
                Place once per FAQ. The script is served from your API origin ({BACKEND_URL || '…'}).
              </p>
              <pre className="text-xs bg-slate-950 text-slate-100 p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all">
                {scriptSnippet}
              </pre>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => {
                  navigator.clipboard.writeText(scriptSnippet);
                  toast.success('Script snippet copied');
                }}
              >
                Copy script snippet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FaqsPage;
